<template>
  <div class="instructors-section">
    <!-- Search Bar -->
    <div class="search-section">
      <div class="search-wrapper">
        <i class="fas fa-search search-icon"></i>
        <input
          v-model="searchText"
          type="text"
          class="search-input"
          :placeholder="$t('instructors.search_placeholder') || 'ابحث عن مدرب...'"
        />
        <button
          v-if="searchText"
          class="search-clear-btn"
          @click="clearSearch"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- حالة التحميل -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner">
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
      </div>
      <p class="loading-text">{{ $t("common.loading") }}</p>
    </div>

    <!-- حالة عدم وجود مدربين -->
    <div v-if="filteredInstructors.length === 0 && !isLoading" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-user-slash"></i>
      </div>
      <h3>{{ $t("instructors.no_instructors") }}</h3>
    </div>

    <!-- عرض المدربين -->
    <div class="instructors-grid" v-if="filteredInstructors.length > 0 && !isLoading">
      <div
        v-for="(instructor, index) in filteredInstructors"
        :key="instructor.id"
        class="instructor-card"
        :style="{ animationDelay: `${index * 0.1}s` }"
      >
        <div class="instructor-avatar-section">
          <div class="avatar-glow"></div>
          <div class="instructor-avatar">
            <img
              :src="instructor.user_img"
              alt="Instructor"
              @error="setDefaultImage"
            />
          </div>
          <div class="verified-badge">
            <i class="fas fa-check"></i>
          </div>
        </div>

        <div class="instructor-info">
          <h4 class="instructor-name">{{ instructor.name }}</h4>
          <div class="instructor-platform">
            <i class="fas fa-graduation-cap"></i>
            <span>{{ $t("instructors.platform_badge") }}</span>
          </div>

          <div class="instructor-rating">
            <div class="stars">
              <i class="fas fa-star" v-for="n in 5" :key="n"></i>
            </div>
          </div>
        </div>

        <a
          :href="`instructor-profile?id=${instructor.id}`"
          class="view-profile-btn"
        >
          <span>{{ $t("instructors.view_profile") }}</span>
          <i class="fas fa-arrow-left"></i>
        </a>
      </div>
    </div>

    <!-- الباجينيشن -->
    <div
      v-if="
        pagination.links && pagination.links.length && instructors.length > 0
      "
      class="pagination-section"
    >
      <div class="pagination-wrapper">
        <button
          class="pagination-btn prev-btn"
          :class="{ disabled: !pagination.prev }"
          @click="changePage(pagination.prev)"
        >
          <i class="fas fa-chevron-right"></i>
          <span>{{ $t("common.prev") }}</span>
        </button>

        <div class="page-numbers">
          <button
            v-for="link in pagination.links"
            :key="link?.label || Math.random()"
            class="page-number"
            :class="{ active: link?.active, disabled: !link?.url }"
            v-if="link && (Number(link.label) || link.active)"
            @click="link?.url && changePage(link.url)"
          >
            {{ link.label }}
          </button>
        </div>

        <button
          class="pagination-btn next-btn"
          :class="{ disabled: !pagination.next }"
          @click="changePage(pagination.next)"
        >
          <span>{{ $t("common.next") }}</span>
          <i class="fas fa-chevron-left"></i>
        </button>
      </div>

      <div class="page-info">
        {{ $t("instructors.page") }} {{ meta.current_page }}
        {{ $t("instructors.of") }} {{ meta.last_page }}
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";

export default {
  data() {
    return {
      instructors: [],
      defaultImage: "/assets/img/acad-icon.png",
      searchText: "",

      isLoading: false,
      pagination: {
        links: [],
        prev: null,
        next: null,
      },
      meta: {
        current_page: 1,
        last_page: 1,
      },
    };
  },
  computed: {
    filteredInstructors() {
      if (!this.searchText.trim()) return this.instructors;
      const search = this.searchText.toLowerCase();
      return this.instructors.filter((instructor) =>
        instructor.name?.toLowerCase().includes(search)
      );
    },
  },
  mounted() {
    this.getInstructors(`${apiUrl}/instructors-v2`);
  },
  methods: {
    setDefaultImage(event) {
      event.target.src = this.defaultImage;
    },
    async getInstructors(url) {
      try {
        this.isLoading = true;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion.phone_code,
          },
        });
        const data = await res.json();

        this.instructors = data.data;

        // تحويل الروابط إلى https
        const convertToHttps = (link) =>
          link ? link.replace(/^http:/, "https:") : null;

        this.pagination.links = data.meta.links.map((linkObj) => ({
          ...linkObj,
          url: convertToHttps(linkObj.url),
        }));

        this.pagination.prev = apiUrl + "/instructors-v2" + data.links.prev;
        this.pagination.next = apiUrl + "/instructors-v2" + data.links.next;

        this.meta = {
          current_page: data.meta.current_page,
          last_page: data.meta.last_page,
        };
      } catch (err) {
        console.error("حدث خطأ أثناء جلب المدربين", err);
      } finally {
        this.isLoading = false;
      }
    },

    changePage(url) {
      if (!url) return;
      this.getInstructors(url);
    },
    clearSearch() {
      this.searchText = "";
    },
  },
};
</script>

<style scoped>
/* ===== Main Section ===== */
.instructors-section {
  padding: 20px 0;
  direction: rtl;
}

/* ===== Search Section ===== */
.search-section {
  margin-bottom: 30px;
}

.search-wrapper {
  position: relative;
  max-width: 600px;
  margin: 0 auto;
}

.search-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #00c744;
  font-size: 18px;
}

.search-input {
  width: 100%;
  padding: 15px 50px 15px 50px;
  border: 2px solid #e2e8f0;
  border-radius: 50px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
}

.search-input:focus {
  outline: none;
  border-color: #00c744;
  box-shadow: 0 0 0 3px rgba(0, 199, 68, 0.1);
}

.search-clear-btn {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  padding: 5px;
  transition: color 0.3s ease;
}

.search-clear-btn:hover {
  color: #00c744;
}

/* ===== Loading State ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  position: relative;
  width: 70px;
  height: 70px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  border-top-color: #00a83b;
  animation-duration: 1s;
}

.spinner-ring:nth-child(3) {
  width: 40%;
  height: 40%;
  top: 30%;
  left: 30%;
  border-top-color: #008f32;
  animation-duration: 0.8s;
}

.loading-text {
  margin-top: 20px;
  color: #00c744;
  font-size: 1rem;
  font-weight: 600;
}

/* ===== Empty State ===== */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.empty-icon i {
  font-size: 40px;
  color: #00c744;
}

.empty-state h3 {
  color: #2d3748;
  font-size: 1.2rem;
  font-weight: 600;
}

/* ===== Instructors Grid ===== */
.instructors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
}

/* ===== Instructor Card ===== */
.instructor-card {
  background: white;
  border-radius: 24px;
  padding: 30px 25px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  position: relative;
  overflow: hidden;
}

.instructor-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 24px 24px 50% 50%;
}

.instructor-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 60px rgba(0, 199, 68, 0.15);
}

/* ===== Avatar Section ===== */
.instructor-avatar-section {
  position: relative;
  margin-bottom: 20px;
  z-index: 1;
}

.avatar-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 130px;
  height: 130px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  opacity: 0.2;
  animation: pulse 2s ease-in-out infinite;
}

.instructor-avatar {
  width: 110px;
  height: 110px;
  margin: 0 auto;
  border-radius: 50%;
  padding: 4px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  position: relative;
}

.instructor-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid white;
}

.verified-badge {
  position: absolute;
  bottom: 5px;
  right: calc(50% - 70px);
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  box-shadow: 0 3px 10px rgba(0, 199, 68, 0.3);
}

.verified-badge i {
  color: white;
  font-size: 12px;
}

/* ===== Instructor Info ===== */
.instructor-info {
  position: relative;
  z-index: 1;
}

.instructor-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 10px 0;
}

.instructor-platform {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  padding: 6px 15px;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #00c744;
  font-weight: 600;
  margin-bottom: 15px;
}

.instructor-platform i {
  font-size: 14px;
}

.instructor-rating {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
}

.stars {
  display: flex;
  gap: 3px;
}

.stars i {
  color: #fbbf24;
  font-size: 14px;
}

.rating-text {
  font-size: 0.85rem;
  color: #718096;
  font-weight: 500;
}

/* ===== View Profile Button ===== */
.view-profile-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 25px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.25);
  position: relative;
  z-index: 1;
}

.view-profile-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(0, 199, 68, 0.35);
  color: white;
}

.view-profile-btn i {
  transition: transform 0.3s ease;
}

.view-profile-btn:hover i {
  transform: translateX(-5px);
}

/* ===== Pagination ===== */
.pagination-section {
  margin-top: 40px;
  text-align: center;
}

.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.pagination-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  border: 2px solid #00c744;
  border-radius: 50px;
  color: #00c744;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-btn:hover:not(.disabled) {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
}

.pagination-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #cbd5e0;
  color: #cbd5e0;
}

.page-numbers {
  display: flex;
  gap: 8px;
}

.page-number {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  border-radius: 12px;
  color: #718096;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
}

.page-number:hover:not(.disabled) {
  background: #e8fff1;
  color: #00c744;
}

.page-number.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  box-shadow: 0 5px 15px rgba(0, 199, 68, 0.3);
}

.page-number.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  margin-top: 15px;
  color: #718096;
  font-size: 0.9rem;
}

/* ===== Animations ===== */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.2;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.3;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Dark Mode Support ===== */
[data-bs-theme="dark"] .instructor-card,
[data-theme="dark"] .instructor-card,
.dark .instructor-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .instructor-card::before,
[data-theme="dark"] .instructor-card::before,
.dark .instructor-card::before {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .instructor-name,
[data-theme="dark"] .instructor-name,
.dark .instructor-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .instructor-platform,
[data-theme="dark"] .instructor-platform,
.dark .instructor-platform {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
  color: #4ade80;
}

[data-bs-theme="dark"] .empty-state,
[data-theme="dark"] .empty-state,
.dark .empty-state {
  background: #1e293b;
}

[data-bs-theme="dark"] .empty-state h3,
[data-theme="dark"] .empty-state h3,
.dark .empty-state h3 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .empty-icon,
[data-theme="dark"] .empty-icon,
.dark .empty-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .pagination-btn,
[data-theme="dark"] .pagination-btn,
.dark .pagination-btn {
  background: #1e293b;
  border-color: #4ade80;
  color: #4ade80;
}

[data-bs-theme="dark"] .page-number,
[data-theme="dark"] .page-number,
.dark .page-number {
  background: #1e293b;
  color: #94a3b8;
}

[data-bs-theme="dark"] .page-number:hover:not(.disabled),
[data-theme="dark"] .page-number:hover:not(.disabled),
.dark .page-number:hover:not(.disabled) {
  background: #0f172a;
  color: #4ade80;
}

[data-bs-theme="dark"] .loading-text,
[data-theme="dark"] .loading-text,
.dark .loading-text {
  color: #4ade80;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .instructors-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
  }

  .instructor-card {
    padding: 25px 20px;
  }

  .pagination-wrapper {
    flex-direction: column;
    gap: 10px;
  }

  .page-numbers {
    order: -1;
  }
}
</style>
